import React, { useMemo } from "react";
import { useSubscriptionContext } from "../../contexts/SubscriptionContext";
import { useUsageTracking } from "../../contexts/UsageTrackingContext";
import { FiTrendingUp, FiZap, FiX, FiFolder, FiCheckSquare } from "../../lib/icons";
import { cn } from "../../lib/utils";
import styles from "./UsageTracker.module.scss";

interface UsageTrackerProps {
  className?: string;
}

export const UsageTracker: React.FC<UsageTrackerProps> = ({ className }) => {
  const { subscription, loading, createCheckoutSession } = useSubscriptionContext();
  const { taskCount, projectCount } = useUsageTracking();

  // Use real-time counts from UsageTrackingContext
  const totalUsage = useMemo(() => {
    return {
      todos: taskCount,
      projects: projectCount,
    };
  }, [taskCount, projectCount]);

  const handleUpgrade = async (planId: string = "plan_pro") => {
    try {
      const checkoutUrl = await createCheckoutSession(planId, "monthly");
      window.open(checkoutUrl, "_blank");
    } catch (error) {
      console.error("Failed to create checkout session:", error);
    }
  };

  if (loading) {
    return <div className={cn(styles.usageTracker, styles.loading, className)} />;
  }

  // Only show for free tier users
  if (
    !subscription ||
    !subscription.plan ||
    !subscription.plan.name ||
    subscription.plan.name !== "free"
  ) {
    return null;
  }

  const plan = subscription.plan;
  const isAtLimit =
    totalUsage.projects >= (plan.max_projects || 0) || totalUsage.todos >= (plan.max_todos || 0);

  return (
    <div className={cn(styles.usageTracker, isAtLimit && styles.atLimit, className)}>
      <div className={styles.header}>
        <div className={styles.leftSection}>
          <div className={cn(styles.statusIcon, isAtLimit ? styles.danger : styles.warning)}>
            {isAtLimit ? <FiX className="w-4 h-4" /> : <FiZap className="w-4 h-4" />}
          </div>

          <div className={styles.content}>
            <div className={styles.titleRow}>
              <h3 className={styles.planTitle}>Free Plan</h3>
              {isAtLimit && <span className={styles.limitBadge}>LIMIT REACHED</span>}
            </div>
            <div className={styles.stats}>
              <div className={styles.statItem}>
                <FiFolder className={styles.statIcon} />
                <span className={styles.statText}>
                  {totalUsage.projects}/{plan.max_projects || 0}
                </span>
              </div>
              <div className={styles.statItem}>
                <FiCheckSquare className={styles.statIcon} />
                <span className={styles.statText}>
                  {totalUsage.todos}/{plan.max_todos || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        <button onClick={() => handleUpgrade("plan_pro")} className={styles.upgradeButton}>
          <FiTrendingUp className={styles.upgradeIcon} />
          Upgrade
        </button>
      </div>

      {isAtLimit && (
        <div className={styles.footer}>
          <p className={styles.footerText}>
            You've reached your free plan limits. Upgrade for unlimited access!
          </p>
        </div>
      )}
    </div>
  );
};

export default UsageTracker;
